import React, {createContext, ReactNode, useCallback, useEffect, useRef, useState} from "react";
import {useForm, UseFormReturn} from "react-hook-form";
import {articleFormSchema, ArticleFormSchemaType} from "../constants/article-form-schema";
import {yupResolver} from "@hookform/resolvers/yup";
import {useArticleApi} from "../../core/hooks/use-article-api";
import {useLoading} from "../../core/hooks/use-loading.hook";
import {mapArticleDetailsToSchema} from "../helpers/map-article-details-to-schema";
import {ArticleDetails} from "../../core/models/interfaces/article-details.interface";
import {mapArticleSchemaToCreate} from "../helpers/map-article-schema-to-create";
import LocalStorageService from "@common/services/local-storage.service";
import {LocalStorageKeys} from "@common/models/enums/local-storage-keys.enum";
import {debounce} from "@utils/debounce";
import {useNavigate} from "react-router-dom";
import {CreateArticleRequest} from "../../core/models/interfaces/create-article-request.interface";
import {ArticleFileManager} from "../helpers/article-file-manager";
import {DateHelper} from "@utils/date-helper";
import {mapArticleSchemaToUpdate} from "../helpers/map-article-schema-to-update";
import {AiGeneratedArticlePayload} from "../models/interfaces/ai-generated-article.interface";

export enum ArticleFormStep {
    CONTENT = "content",
    SPECIALIST = "specialist",
    RELATED = "related",
    AUTHOR = "author",
    EXERCISES = "exercises",
}

interface StepMetadata {
    isDirty: boolean;
    editedAt: string | null;
    hasBeenVisited: boolean;
}

export type StepsMetadata = Record<ArticleFormStep, StepMetadata>;

interface ArticleFormContextType {
    stepsMetadata: StepsMetadata;
    currentStep: ArticleFormStep;
    setCurrentStep: (step: ArticleFormStep) => void;
    updateStepData: <K extends keyof ArticleFormSchemaType>(
        step: ArticleFormStep,
        data: Pick<ArticleFormSchemaType, K>
    ) => void;
    markStepAsSaved: (step: ArticleFormStep) => void;
    markStepAsVisited: (step: ArticleFormStep) => void;
    isStepDirty: (step: ArticleFormStep) => boolean;
    hasUnsavedChanges: () => boolean;
    resetForm: () => void;
    clearForm: () => void;
    loadArticleById: (articleId: string) => Promise<void>;
    loadDraftArticle: () => void;
    formData: ArticleFormSchemaType;
    form: UseFormReturn<ArticleFormSchemaType>;
    isEditMode: boolean;
    isUniversal: boolean;
    isPostoperativeUniversal: boolean;
    submitArticle: () => Promise<void>;
    articleId?: string;
    appId?: string;
    loading: boolean;
    getStepErrors: (step: ArticleFormStep) => string[];
    hasStepErrors: (step: ArticleFormStep) => boolean;
    clearStepErrors: (step: ArticleFormStep) => void;
    resetVisitedSteps: () => void;
    hideArticle: () => Promise<void>;
    resetVersionsToPublished: () => Promise<void>;
    areVersionIdentical: boolean;
    deleteArticle: (id: string) => Promise<void>;
    deleteDraftArticle: () => Promise<void>;
    startNewArticleFromAi: (payload: AiGeneratedArticlePayload) => void;
    applyAiContentToCurrentForm: (payload: AiGeneratedArticlePayload) => void;
}

export const ArticleFormContext = createContext<ArticleFormContextType | null>(null);

const initialStepsMetadata: StepsMetadata = {
    [ArticleFormStep.CONTENT]: {isDirty: false, editedAt: null, hasBeenVisited: true},
    [ArticleFormStep.SPECIALIST]: {isDirty: false, editedAt: null, hasBeenVisited: false},
    [ArticleFormStep.RELATED]: {isDirty: false, editedAt: null, hasBeenVisited: false},
    [ArticleFormStep.AUTHOR]: {isDirty: false, editedAt: null, hasBeenVisited: false},
    [ArticleFormStep.EXERCISES]: {isDirty: false, editedAt: null, hasBeenVisited: false},
};

interface ArticleFormProviderProps {
    children: ReactNode;
    initialData?: ArticleFormSchemaType;
}

const defaultFormValues: ArticleFormSchemaType = {
    isVisible: false,
    title: "",
    summary: "",
    content: "",
    authorId: "",
    authorName: "",
    specialists: [],
    tags: [],
    relatedArticles: [],
    exercises: [],
};

export const ArticleFormProvider: React.FC<ArticleFormProviderProps> = (props) => {
    const {children, initialData} = props;

    const [stepsMetadata, setStepsMetadata] = useState<StepsMetadata>(initialStepsMetadata);
    const [currentStep, setCurrentStep] = useState<ArticleFormStep>(ArticleFormStep.CONTENT);
    const [articleId, setArticleId] = useState<string | undefined>(undefined);
    const [areVersionIdentical, setAreVersionIdentical] = useState<boolean>(true);
    const [isUniversal, setIsUniversal] = useState<boolean>(false);
    const [isPostoperativeUniversal, setIsPostoperativeUniversal] = useState<boolean>(false);
    const [appId, setAppId] = useState<string | undefined>(undefined);

    const articleIdRef = useRef<string | undefined>(undefined);
    const canAutoSaveRef = useRef<boolean>(false);

    const navigate = useNavigate();
    const {
        getArticle: getArticleApi,
        updateArticle: updateArticleApi,
        createArticle: createArticleApi,
        publishArticle: publishArticleApi,
        resetVersionsToPublished: resetVersionsToPublishedApi,
        deleteArticle: deleteArticleApi,
    } = useArticleApi();
    const {isLoading} = useLoading();

    const form = useForm<ArticleFormSchemaType>({
        resolver: yupResolver(articleFormSchema) as any,
        defaultValues: initialData || defaultFormValues,
        mode: "onSubmit",
    });

    const watchSubscription = useRef<ReturnType<typeof form.watch> | null>(null);
    const [formData, setFormData] = useState<ArticleFormSchemaType>(form.getValues());

    useEffect(() => {
        articleIdRef.current = articleId;
    }, [articleId]);

    const updateStepData = (step: ArticleFormStep, updates: Partial<StepMetadata>): void => {
        setStepsMetadata((prev) => ({
            ...prev,
            [step]: {
                ...prev[step],
                ...updates,
            },
        }));
    };

    const markStepAsSaved = (step: ArticleFormStep): void => {
        setStepsMetadata((prev) => ({
            ...prev,
            [step]: {
                ...prev[step],
                isDirty: false,
                editedAt: new Date().toISOString(),
            },
        }));
    };

    const markStepAsVisited = (step: ArticleFormStep): void => {
        setStepsMetadata((prev) => ({
            ...prev,
            [step]: {
                ...prev[step],
                hasBeenVisited: true,
            },
        }));
    };

    const isStepDirty = (step: ArticleFormStep) => stepsMetadata[step].isDirty;

    const hasUnsavedChanges = () => Object.values(stepsMetadata).some((meta) => meta.isDirty);

    const resetForm = (): void => {
        setStepsMetadata(initialStepsMetadata);
        setCurrentStep(ArticleFormStep.CONTENT);
        setArticleId(undefined);
        articleIdRef.current = undefined;
        canAutoSaveRef.current = false;
        watchSubscription.current?.unsubscribe();
    };

    const clearForm = (): void => {
        resetForm();
        ArticleFileManager.clearAllFiles();
        form.reset(defaultFormValues);
        setFormData(form.getValues());
    };

    const getStepFields = (step: ArticleFormStep): string[] => {
        switch (step) {
            case ArticleFormStep.CONTENT:
                return ['title', 'summary', 'content', 'tags'];
            case ArticleFormStep.SPECIALIST:
                return ['specialists'];
            case ArticleFormStep.RELATED:
                return ['relatedArticles'];
            case ArticleFormStep.AUTHOR:
                return ['authorId', 'authorName'];
            case ArticleFormStep.EXERCISES:
                return ['exercises'];
            default:
                return [];
        }
    };

    const getStepErrors = (step: ArticleFormStep): string[] => {
        const errors = form.formState.errors;
        const stepFields: string[] = getStepFields(step);

        return stepFields
            .map(field => errors[field as keyof typeof errors])
            .filter(Boolean)
            .map(error => error?.message || 'Błąd walidacji');
    };

    const hasStepErrors = (step: ArticleFormStep): boolean => getStepErrors(step).length > 0;

    const clearStepErrors = (step: ArticleFormStep): void => {
        getStepFields(step).forEach(field => form.clearErrors(field as keyof ArticleFormSchemaType));
    };

    const resetVisitedSteps = (): void => {
        setStepsMetadata((prev) => {
            const updated = {...prev};
            Object.keys(updated).forEach(step => {
                updated[step as ArticleFormStep] = {...updated[step as ArticleFormStep], hasBeenVisited: false};
            });
            return updated;
        });
    };

    const saveDraftArticle = (data: ArticleFormSchemaType): void => {
        if (!canAutoSaveRef.current) {
            return;
        }
        if (!articleIdRef.current) {
            const editedDate = DateHelper.formatDate(new Date(), "YYYY-MM-DDTHH:mm:ss");
            LocalStorageService.save(LocalStorageKeys.EDITED_DRAFT_ARTICLE, editedDate);
            updateStepData(ArticleFormStep.CONTENT, {editedAt: editedDate});
            LocalStorageService.save(LocalStorageKeys.DRAFT_ARTICLE, data);
        }
    };

    const startNewArticle = (): void => {
        setArticleId(undefined);
        articleIdRef.current = undefined;
        disableAutoSave();

        ArticleFileManager.setMode('draft');

        form.reset(defaultFormValues);
        setFormData(form.getValues());
        queueMicrotask(enableAutoSave);
    };

    const loadDraftArticle = (): void => {
        articleIdRef.current = undefined;
        setArticleId(undefined);
        setCurrentStep(ArticleFormStep.CONTENT);
        disableAutoSave();

        ArticleFileManager.setMode('draft');

        const saved =
            LocalStorageService.get<ArticleFormSchemaType | null>(
                LocalStorageKeys.DRAFT_ARTICLE
            ) ?? null;

        if (saved) {
            form.reset(saved as ArticleFormSchemaType);
            const editedDate = LocalStorageService.get(LocalStorageKeys.EDITED_DRAFT_ARTICLE);
            updateStepData(ArticleFormStep.CONTENT, {editedAt: editedDate});
            setFormData(form.getValues());
            queueMicrotask(enableAutoSave);
        } else {
            startNewArticle();
        }
    };

    const startNewArticleFromAi = (payload: AiGeneratedArticlePayload): void => {
        articleIdRef.current = undefined;
        setArticleId(undefined);
        setCurrentStep(ArticleFormStep.CONTENT);
        disableAutoSave();

        ArticleFileManager.setMode('draft');
        ArticleFileManager.clearAllFiles();
        LocalStorageService.remove(LocalStorageKeys.DRAFT_ARTICLE);
        LocalStorageService.remove(LocalStorageKeys.EDITED_DRAFT_ARTICLE);

        const aiDraftData: ArticleFormSchemaType = {
            ...defaultFormValues,
            title: payload.title,
            summary: payload.abstract,
            content: payload.content,
            tags: payload.tags ?? [],
        };

        form.reset(aiDraftData);
        LocalStorageService.save(LocalStorageKeys.DRAFT_ARTICLE, aiDraftData);
        const editedAt = DateHelper.formatDate(new Date(), "YYYY-MM-DDTHH:mm:ss");
        LocalStorageService.save(LocalStorageKeys.EDITED_DRAFT_ARTICLE, editedAt);
        updateStepData(ArticleFormStep.CONTENT, {editedAt});

        queueMicrotask(enableAutoSave);
    };

    const applyAiContentToCurrentForm = (payload: AiGeneratedArticlePayload): void => {
        form.setValue("title", payload.title, {shouldDirty: true, shouldTouch: true});
        form.setValue("summary", payload.abstract, {shouldDirty: true, shouldTouch: true});
        form.setValue("content", payload.content, {shouldDirty: true, shouldTouch: true});
        if (payload.tags && payload.tags.length > 0) {
            form.setValue("tags", payload.tags, {shouldDirty: true, shouldTouch: true});
        }
        ArticleFileManager.syncUsedFilesFromContent(payload.content);
    };

    const loadArticleById = async (id: string): Promise<void> => {
        try {
            setArticleId(id);
            articleIdRef.current = id;
            disableAutoSave();
            const articleData: ArticleDetails = await getArticleApi(id);

            ArticleFileManager.initializeFilesFromArticle(articleData.allFiles ?? []);

            setAppId(articleData.appId);
            updateStepData(ArticleFormStep.CONTENT, {editedAt: new Date(articleData.editedAt).toISOString()});
            setAreVersionIdentical(articleData.areVersionIdentical);
            setIsUniversal(articleData.isUniversal);
            setIsPostoperativeUniversal(articleData.isPostoperative);
            form.reset(mapArticleDetailsToSchema(articleData));
            setFormData(form.getValues());
            queueMicrotask(enableAutoSave);
            setCurrentStep(ArticleFormStep.CONTENT);
        } catch (error) {
            console.error("❌ Error loading article:", error);
        }
    };

    const updateArticle = async (data: ArticleFormSchemaType): Promise<void> => {
        try {
            if (articleIdRef.current) {
                if (!data.title || !data.content) {
                    console.log("⚠️ Skipping update - empty data detected");
                    return;
                }

                disableAutoSave();

                const mappedData: CreateArticleRequest = mapArticleSchemaToUpdate(data);
                const updatedArticle = await updateArticleApi({id: articleIdRef.current, ...mappedData});

                setAreVersionIdentical(updatedArticle.areVersionIdentical);
                setIsUniversal(updatedArticle.isUniversal);
                setIsPostoperativeUniversal(updatedArticle.isPostoperative);
                updateStepData(ArticleFormStep.CONTENT, {
                    editedAt: new Date(updatedArticle.editedAt).toISOString()
                });
                form.resetField("content", {defaultValue: updatedArticle.content});
                form.resetField("title", {defaultValue: updatedArticle.title});
                form.resetField("summary", {defaultValue: updatedArticle.summary});
                setFormData(form.getValues());

                queueMicrotask(enableAutoSave);
            }
        } catch (err) {
            console.error("❌ Error update article:", err);
        }
    };

    const createNewArticle = async (data: ArticleFormSchemaType): Promise<void> => {
        try {
            const mappedData: CreateArticleRequest = mapArticleSchemaToCreate(data);
            await createArticleApi({...mappedData, isVisible: true});
            LocalStorageService.remove(LocalStorageKeys.DRAFT_ARTICLE);
            LocalStorageService.remove(LocalStorageKeys.EDITED_DRAFT_ARTICLE);
        } catch (err) {
            console.error("❌ Error saving new article:", err);
            throw err;
        }
    };

    const deleteDraftArticle = async (): Promise<void> => {
        try {
            LocalStorageService.remove(LocalStorageKeys.DRAFT_ARTICLE);
            LocalStorageService.remove(LocalStorageKeys.EDITED_DRAFT_ARTICLE);

            clearForm();
        } catch (err) {
            console.error("❌ Error deleting draft article:", err);
        }
    }

    const deleteArticle = async (id: string): Promise<void> => {
        try {
            await deleteArticleApi(id);
            navigate("/app/articles", {replace: true});
        } catch (err) {
            console.error("❌ Error deleting edited article:", err);
        }
    }

    const publishArticle = async (data: ArticleFormSchemaType): Promise<void> => {
        try {
            const mappedData: CreateArticleRequest = mapArticleSchemaToCreate(data);
            await publishArticleApi({...mappedData, id: articleIdRef.current!, isVisible: true});
            setAreVersionIdentical(true);
        } catch (err) {
            console.error("❌ Error saving new article:", err);
            throw err;
        }
    };

    const resetVersionsToPublished = async (): Promise<void> => {
        try {
            if (document.activeElement && document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }

            await new Promise(resolve => setTimeout(resolve, 500));

            await resetVersionsToPublishedApi(articleIdRef.current!);
            await loadArticleById(articleIdRef.current!);
        } catch (err) {
            console.error("❌ Error saving new article:", err);
        }
    }

    const hideArticle = async (): Promise<void> => {
        try {
            const mappedData: CreateArticleRequest = mapArticleSchemaToCreate(formData);
            await updateArticleApi({id: articleIdRef.current!, ...mappedData, isVisible: false});
            form.setValue("isVisible", false);
        } catch (err) {
            console.error("❌ Error hiding article:", err);
        }
    };

    const debounceSaveDraft = useCallback(debounce(saveDraftArticle, 1000), [articleId]);
    const debounceSave = useCallback(debounce(updateArticle, 1000), [articleId]);

    const subscribeWatch = useCallback(() => {
        watchSubscription.current?.unsubscribe();

        watchSubscription.current = form.watch((values) => {
            setFormData(values as ArticleFormSchemaType);

            if (!canAutoSaveRef.current) return;

            if (articleIdRef.current) void debounceSave(values as ArticleFormSchemaType);
            else debounceSaveDraft(values as ArticleFormSchemaType);
        });
    }, [form, debounceSave, debounceSaveDraft]);

    const enableAutoSave = useCallback(() => {
        canAutoSaveRef.current = true;
        subscribeWatch();
    }, [subscribeWatch]);

    const disableAutoSave = useCallback(() => {
        canAutoSaveRef.current = false;
        watchSubscription.current?.unsubscribe();
        watchSubscription.current = null;
    }, []);

    const submitArticle = form.handleSubmit(async (data): Promise<void> => {
        if (articleIdRef.current) {
            await publishArticle(data);
            clearForm();
            navigate("/app/articles");
        } else {
            await createNewArticle(data);
            clearForm();
            navigate("/app/articles");
        }
    });

    useEffect(() => {
        return () => watchSubscription.current?.unsubscribe();
    }, []);

    return (
        <ArticleFormContext.Provider
            value={{
                stepsMetadata,
                currentStep,
                areVersionIdentical,
                setCurrentStep,
                updateStepData,
                markStepAsSaved,
                markStepAsVisited,
                isStepDirty,
                hasUnsavedChanges,
                resetForm,
                clearForm,
                loadArticleById,
                loadDraftArticle,
                hideArticle,
                isUniversal,
                isPostoperativeUniversal,
                submitArticle,
                resetVisitedSteps,
                resetVersionsToPublished,
                deleteArticle,
                deleteDraftArticle,
                startNewArticleFromAi,
                applyAiContentToCurrentForm,
                formData,
                isEditMode: !!articleIdRef.current,
                form,
                articleId: articleIdRef.current,
                appId: appId,
                loading: isLoading("getArticle"),
                getStepErrors,
                hasStepErrors,
                clearStepErrors,
            }}
        >
            {children}
        </ArticleFormContext.Provider>
    );
};
